<template>
    <article
        class="restaurant-card"
        :class="{ clickable: clickable && !disabled, disabled }"
        :role="clickable && !disabled ? 'button' : undefined"
        :tabindex="clickable && !disabled ? 0 : -1"
        :aria-label="restaurant.name"
        :title="restaurant.name"
        @click="accessEstablishment"
        @keydown.enter.prevent="accessEstablishment"
        @keydown.space.prevent="accessEstablishment">
        <div class="restaurant-card-logo" :class="{ background: restaurant.logo_url }" :style="computedStyle">
            <feather v-if="!restaurant.logo_url" type="home" />
        </div>
        <div class="restaurant-card-content">
            <span class="restaurant-card-status">{{ disabled ? "Accès désactivé" : "Établissement" }}</span>
            <h6 data-test-id="title-restaurant_name">{{ restaurant.name }}</h6>
        </div>
        <span v-if="disabled" class="restaurant-card-disabled-badge">Désactivé</span>
        <feather v-else class="restaurant-card-arrow" type="chevron-right" />
    </article>
</template>

<script>
export default {
    name: "RestaurantCard",
    props: {
        restaurant: {
            required: true,
        },
        clickable: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        computedStyle() {
            if (this.restaurant.logo_url) {
                return `background-image: url("${this.restaurant.logo_url}");`;
            }

            return ``;
        },
    },
    methods: {
        accessEstablishment() {
            if (this.disabled) {
                return;
            }
            this.$emit("click", this.restaurant);
        },
    },
};
</script>

<style lang="scss" scoped>
.restaurant-card {
    position: relative;
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr) 18px;
    gap: 16px;
    align-items: center;
    min-height: 112px;
    padding: 18px;
    overflow: hidden;
    background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
    border: 1px solid rgba(8, 62, 96, 0.11);
    border-radius: 16px;
    box-shadow: 0 18px 40px rgba(8, 62, 96, 0.07);
    transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.restaurant-card::before {
    position: absolute;
    top: 0;
    right: 18px;
    left: 18px;
    height: 2px;
    content: "";
    background: linear-gradient(90deg, rgba(8, 62, 96, 0), rgba(8, 62, 96, 0.28), rgba(8, 62, 96, 0));
    opacity: 0;
    transition: opacity 0.18s ease;
}

.restaurant-card.clickable {
    cursor: pointer;
}

.restaurant-card.clickable:focus {
    border-color: rgba(8, 62, 96, 0.3);
    box-shadow: 0 0 0 3px rgba(8, 62, 96, 0.12), 0 22px 54px rgba(8, 62, 96, 0.12);
    outline: none;
}

.restaurant-card.clickable:hover {
    border-color: rgba(8, 62, 96, 0.22);
    box-shadow: 0 22px 54px rgba(8, 62, 96, 0.12);
    transform: translateY(-3px);
}

.restaurant-card.clickable:hover::before {
    opacity: 1;
}

.restaurant-card.disabled {
    cursor: not-allowed;
    background: linear-gradient(180deg, #f8fafc 0%, #eef3f6 100%);
    border-color: rgba(148, 163, 184, 0.34);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.restaurant-card.disabled::after {
    position: absolute;
    inset: 0;
    pointer-events: none;
    content: "";
    background: repeating-linear-gradient(135deg, rgba(148, 163, 184, 0.08) 0, rgba(148, 163, 184, 0.08) 8px, transparent 8px, transparent 16px);
    opacity: 0.45;
}

.restaurant-card.disabled .restaurant-card-logo {
    color: #73808f;
    background: linear-gradient(145deg, #dbe3e8, #c9d3da);
    box-shadow: none;
    filter: grayscale(1);
}

.restaurant-card.disabled .restaurant-card-status {
    color: #8a4b09;
}

.restaurant-card.disabled h6 {
    color: #667085;
}

.restaurant-card-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    color: #ffffff;
    background: linear-gradient(145deg, #083e60, #0b5d87);
    background-position: center;
    background-size: cover;
    border-radius: 14px;
    box-shadow: 0 14px 28px rgba(8, 62, 96, 0.18);
}

.restaurant-card-logo .feather {
    width: 30px;
    height: 30px;
}

.restaurant-card-content {
    min-width: 0;
}

.restaurant-card-status {
    display: inline-flex;
    margin-bottom: 7px;
    color: #0b5d87;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
}

.restaurant-card h6 {
    margin: 0;
    overflow: hidden;
    color: #1d2939;
    font-size: 16px;
    font-weight: 800;
    line-height: 1.3;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
}

.restaurant-card-arrow {
    width: 18px;
    height: 18px;
    color: #98a2b3;
    transition: color 0.18s ease, transform 0.18s ease;
}

.restaurant-card-disabled-badge {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 5px 9px;
    color: #8a4b09;
    font-size: 11px;
    font-weight: 800;
    background: #fff4d6;
    border: 1px solid rgba(217, 119, 6, 0.22);
    border-radius: 999px;
}

.restaurant-card.clickable:hover .restaurant-card-arrow {
    color: #083e60;
    transform: translateX(2px);
}
</style>
